import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getInitialAppRouteState,
  persistSessionAppRoute,
  type SessionAppRoutePersisted,
} from "@/app/utils/session-app-route";
import { useDarkMode } from "@/app/hooks/use-dark-mode";
import * as authClient from "@/app/api/auth-client";
import type { Category } from "@/app/api/auth-client";
import type { Ad } from "@/app/components/ad-card";
import type { FavoriteItem } from "@/app/components/favorites-page";
import type { CartItem } from "@/app/components/cart-page";
import type { Address } from "@/app/components/checkout-address-page";
import type { PaymentMethod } from "@/app/components/checkout-payment-page";
import type { ShippingOption } from "@/app/components/checkout-shipping-page";
import type { City } from "@/app/components/location-selector-modal";
import type { Conversation } from "@/app/components/chat-list-page";
import type { SortOption } from "@/app/components/sort-sheet";
import type { CustomFilters } from "@/app/components/custom-filter-sheet";
import type { OnboardingData } from "@/app/components/new-user-onboarding";

export interface ChatMessage {
  id: string;
  conversationId: string;
  text: string;
  sender: "user" | "other";
  timestamp: Date;
  messageType?: string;
  offerData?: {
    offerId: string;
    offerType: "cash-only" | "product-plus-cash";
    totalOfferValue: number;
    targetAdTitle: string;
    targetAdId: string;
    status: "pending" | "accepted" | "rejected" | "countered";
  };
}

export interface ReceivedOffer {
  id: string;
  buyerName: string;
  buyerAvatar: string;
  targetAd: Ad;
  offerType: "cash-only" | "product-plus-cash";
  cashAmount: number;
  tradeInAds?: Array<{ id: string; value: number; title: string; image: string }>;
  message?: string;
  totalOfferValue: number;
  status: "pending" | "accepted" | "rejected" | "withdrawn" | "countered";
  createdAt: Date;
}

export interface SentOffer {
  id: string;
  sellerName: string;
  sellerAvatar: string;
  targetAd: Ad;
  offerType: "cash-only" | "product-plus-cash";
  cashAmount: number;
  tradeInAds?: Array<{ id: string; value: number; title: string; image: string }>;
  message?: string;
  totalOfferValue: number;
  status: "pending" | "accepted" | "rejected" | "counter-offered" | "in-cart" | "completed" | "fulfilled" | "expired" | "cancelled";
  createdAt: Date;
  acceptedAt?: Date;
  expiresAt?: Date;
  updates: Array<{
    type: "sent" | "counter-offer-received" | "accepted" | "rejected";
    message?: string;
    counterOfferAmount?: number;
    timestamp: Date;
  }>;
}

export type PageType = "home" | "login" | "notifications" | "addresses" | "my-ads" | "favorites" | "ad-detail" | "seller-profile" | "profile" | "edit-profile" | "cart" | "checkout-address" | "checkout-shipping" | "checkout-payment" | "checkout-installments" | "checkout-review" | "checkout-stripe" | "checkout-second-party" | "order-confirmation" | "order-detail" | "chat" | "chat-conversation" | "category-results" | "settings" | "help" | "troca-via-hub" | "onboarding" | "wallet" | "password-security" | "payment-methods" | "withdraw" | "language-region" | "privacy" | "data-usage" | "about" | "terms" | "privacy-policy" | "account-deletion" | "make-offer" | "received-offers" | "sent-offers" | "sales" | "purchases";

/** Checkout: PIX + boleto only until saved cards are loaded from a real source. */
const INITIAL_PAYMENT_METHODS: PaymentMethod[] = [
  {
    id: "pix",
    type: "pix",
    isDefault: true,
  },
  {
    id: "boleto",
    type: "boleto",
    isDefault: false,
  },
];

export function useAppState(initialAds: Ad[]) {
  const initialRoute = useMemo(() => getInitialAppRouteState(), []);
  const [ads, setAds] = useState<Ad[]>(initialAds);
  const [searchQuery, setSearchQuery] = useState(initialRoute.searchQuery);
  const [selectedCategory, setSelectedCategory] = useState(initialRoute.selectedCategory);
  /** Legacy: kept for prop compatibility; display uses only favoriteAdIds + API. No mock/temporary favorites. */
  const [favoriteItems, setFavoriteItems] = useState<FavoriteItem[]>([]);
  /** Favorites from API (ids + created_at). Hydrated on login; used for isFavorite and Favorites page. */
  const [favoriteAdIds, setFavoriteAdIds] = useState<string[]>([]);
  const [favoriteCreatedAt, setFavoriteCreatedAt] = useState<Record<string, string>>({});
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  /** Cold URL (?stripe_return / ?ad=) wins; otherwise restore last screen from sessionStorage after reload. */
  const [currentPage, setCurrentPage] = useState<PageType>(initialRoute.currentPage);
  const [navigationHistory, setNavigationHistory] = useState<PageType[]>(initialRoute.navigationHistory);
  /** When set, back button goes here instead of popping history (e.g. ad-detail from list, checkout steps). Cleared when used. */
  const [returnToPage, setReturnToPage] = useState<PageType | null>(initialRoute.returnToPage);
  const [selectedAdId, setSelectedAdId] = useState<string | null>(initialRoute.selectedAdId);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(initialRoute.selectedOrderId);
  /** When on order-detail page, where to go back: "purchases" or "sales". */
  const [orderDetailReturnPage, setOrderDetailReturnPage] = useState<"purchases" | "sales">(
    initialRoute.orderDetailReturnPage
  );
  const [selectedSellerName, setSelectedSellerName] = useState<string | null>(initialRoute.selectedSellerName);
  const [selectedSellerId, setSelectedSellerId] = useState<string | null>(initialRoute.selectedSellerId);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  
  // Dark mode state
  const darkMode = useDarkMode();
  
  // Chat state (loaded from BFF when user opens Mensagens)
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(
    initialRoute.selectedConversationId
  );
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  
  // Offers state
  const [receivedOffers, setReceivedOffers] = useState<ReceivedOffer[]>([]);
  const [sentOffers, setSentOffers] = useState<SentOffer[]>([]);
  const [selectedOfferId, setSelectedOfferId] = useState<string | null>(initialRoute.selectedOfferId ?? null);
  
  // Checkout flow state (addresses loaded from Bonamassa profile on login/session restore)
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(INITIAL_PAYMENT_METHODS);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>("pix");
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  const [selectedShippingId, setSelectedShippingId] = useState<string | null>(null);
  const [shippingOptionsLoading, setShippingOptionsLoading] = useState(false);
  const [shippingOptionsError, setShippingOptionsError] = useState<string | null>(null);
  /** Hub city/state from BFF/config-platform for route visualization (loaded when hub routing is used). */
  const [hubDisplay, setHubDisplay] = useState<{ city: string; state: string } | null>(null);
  const [selectedInstallments, setSelectedInstallments] = useState<number>(1);
  const [lastOrderNumber, setLastOrderNumber] = useState<string>(initialRoute.lastOrderNumber);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [orderConfirming, setOrderConfirming] = useState(false);
  /** Stripe payment step: set after create order + get payment-intent; cleared when payment succeeds or user leaves. */
  const [pendingPaymentOrderId, setPendingPaymentOrderId] = useState<string | null>(null);
  const [pendingPaymentClientSecret, setPendingPaymentClientSecret] = useState<string | null>(null);
  const [pendingPaymentPublishableKey, setPendingPaymentPublishableKey] = useState<string>("");
  /** When set, user is in second-party checkout (swap: pay only shipping). Cleared when leaving the flow. */
  const [secondPartyPaymentOrderId, setSecondPartyPaymentOrderId] = useState<string | null>(null);
  /** When set, user is on checkout-review to pay this existing order (from order-detail). Cleared when leaving or after opening Stripe. */
  const [payOrderId, setPayOrderId] = useState<string | null>(null);
  /** When set, cart/checkout is tied to this order (restore from it when opening cart; confirm = pay this order). Persisted to localStorage. Cleared on payment success. */
  const [cartOrderId, setCartOrderIdState] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    try {
      return window.localStorage.getItem("ezyhub_cart_order_id") || null;
    } catch {
      return null;
    }
  });
  const setCartOrderId = (id: string | null) => {
    setCartOrderIdState(id);
    try {
      if (typeof window !== "undefined") {
        if (id) window.localStorage.setItem("ezyhub_cart_order_id", id);
        else window.localStorage.removeItem("ezyhub_cart_order_id");
      }
    } catch {
      // ignore
    }
  };

  // Cart toast state
  const [cartToastProduct, setCartToastProduct] = useState<string>("");
  // Shown when add-to-cart is blocked (mixed cart / multiple offers)
  const [cartBlockMessage, setCartBlockMessage] = useState<string | null>(null);

  // Sort state
  const [currentSort, setCurrentSort] = useState<SortOption>("newest");

  // Custom filters state
  const [customFilters, setCustomFilters] = useState<CustomFilters>({
    brands: [],
    conditions: [],
    priceMin: "",
    priceMax: "",
    locations: [],
  });

  // Server-side search results (category-results page): ads + cursor for load more
  const [searchResultsAds, setSearchResultsAds] = useState<Ad[]>([]);
  const [searchNextCursor, setSearchNextCursor] = useState<string | null>(null);
  const [searchResultsLoading, setSearchResultsLoading] = useState(false);

  // Categories that have at least one active ad (for home carousel filter). null = not loaded yet.
  const [categoriesWithAds, setCategoriesWithAds] = useState<string[] | null>(null);
  /** Same categories sorted by active ad count (desc), from API. null until loaded. */
  const [categoriesWithAdsRanked, setCategoriesWithAdsRanked] = useState<
    authClient.CategoryRankedByAds[] | null
  >(null);
  const [categoriesWithAdsFetchedAt, setCategoriesWithAdsFetchedAt] = useState<number | null>(null);
  /** Catalog (from getCategories); used to expand banner click and to resolve category_id → name for display. */
  const [categoryCatalog, setCategoryCatalog] = useState<Category[] | null>(null);

  const loadSearchResults = useCallback(() => {
    // Avoid showing the previous page's rows (e.g. unfiltered "Todos") while the new request is in flight.
    setSearchResultsAds([]);
    setSearchNextCursor(null);
    setSearchResultsLoading(true);
    // Send only category_id: BFF expands to parent+descendants from product-master (avoids broken client-only category_ids lists).
    const categoryIdParam = selectedCategory !== "Todos" ? selectedCategory : undefined;
    authClient
      .getAdsSearch({
        q: searchQuery.trim() || undefined,
        category_id: categoryIdParam,
        limit: 20,
      })
      .then(({ ads, next_cursor }) => {
        setSearchResultsAds(ads);
        setSearchNextCursor(next_cursor);
      })
      .catch(() => {
        setSearchResultsAds([]);
        setSearchNextCursor(null);
      })
      .finally(() => setSearchResultsLoading(false));
  }, [searchQuery, selectedCategory]);

  const loadMoreSearchResults = useCallback(() => {
    if (!searchNextCursor) return;
    setSearchResultsLoading(true);
    const categoryIdParam = selectedCategory !== "Todos" ? selectedCategory : undefined;
    authClient
      .getAdsSearch({
        q: searchQuery.trim() || undefined,
        category_id: categoryIdParam,
        limit: 20,
        cursor: searchNextCursor,
      })
      .then(({ ads, next_cursor }) => {
        setSearchResultsAds((prev) => [...prev, ...ads]);
        setSearchNextCursor(next_cursor);
      })
      .catch(() => {})
      .finally(() => setSearchResultsLoading(false));
  }, [searchQuery, selectedCategory, searchNextCursor]);

  const loadCategoriesWithAds = useCallback(() => {
    Promise.all([
      authClient.getCategoriesWithAds(),
      authClient.getCategories().catch(() => [] as Category[]),
    ])
      .then(([res, catalog]) => {
        const ids = res.category_ids;
        const ranked = res.categories_ranked ?? [];
        const expanded =
          catalog.length > 0
            ? authClient.expandCategoryIdsWithAncestors(ids, catalog)
            : ids;
        setCategoriesWithAds(expanded);
        setCategoriesWithAdsRanked(ranked);
        setCategoryCatalog(catalog.length > 0 ? catalog : null);
        setCategoriesWithAdsFetchedAt(Date.now());
      })
      .catch(() => {
        setCategoriesWithAds([]);
        setCategoriesWithAdsRanked([]);
        setCategoriesWithAdsFetchedAt(Date.now());
      });
  }, []);

  /** Clear cache so next home visit refetches (e.g. after create/delete ad). */
  const invalidateCategoriesWithAds = useCallback(() => {
    setCategoriesWithAds(null);
    setCategoriesWithAdsRanked(null);
    setCategoriesWithAdsFetchedAt(null);
  }, []);

  // UI state consolidated
  const [uiState, setUIState] = useState({
    isModalOpen: false,
    isMenuOpen: false,
    isChatModalOpen: false,
    isLocationSelectorOpen: false,
    isSortSheetOpen: false,
    isCustomFilterOpen: false,
    showCartToast: false,
  });

  // Track if current navigation came from menu (for restoring menu state on back)
  const [navigatedFromMenu, setNavigatedFromMenu] = useState(false);

  // User profile state
  const [userProfile, setUserProfile] = useState<OnboardingData | null>(null);
  const [userId, setUserId] = useState<string>("");
  const [userEmail, setUserEmail] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  /** GET /auth/me `email_verified`; used with GET /auth/me/profile so UI is not stale vs user doc. */
  const [authEmailVerified, setAuthEmailVerified] = useState<boolean | undefined>(undefined);
  const [userProfilePicture, setUserProfilePicture] = useState<string>("");
  const [needsOnboarding, setNeedsOnboarding] = useState(false);


  // When true: show Home even when not logged in (default). When false: show LoginPage. "Entrar" sets this to false.
  const [browsingAfterSignup, setBrowsingAfterSignup] = useState(true);

  // True while we check stored tokens and restore session on load (avoids flashing login then app)
  const [authCheckPending, setAuthCheckPending] = useState(() =>
    !!(authClient.getAccessToken() || authClient.getRefreshToken())
  );

  // Notifications unread count (from BFF GET /auth/me/notifications or unread-count). Used for bell badge.
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);

  // Update individual UI state
  const setIsModalOpen = (value: boolean) => setUIState(prev => ({ ...prev, isModalOpen: value }));
  const setIsMenuOpen = (value: boolean) => setUIState(prev => ({ ...prev, isMenuOpen: value }));
  const setIsChatModalOpen = (value: boolean) => setUIState(prev => ({ ...prev, isChatModalOpen: value }));
  const setIsLocationSelectorOpen = (value: boolean) => setUIState(prev => ({ ...prev, isLocationSelectorOpen: value }));
  const setIsSortSheetOpen = (value: boolean) => setUIState(prev => ({ ...prev, isSortSheetOpen: value }));
  const setIsCustomFilterOpen = (value: boolean) => setUIState(prev => ({ ...prev, isCustomFilterOpen: value }));
  const setShowCartToast = (value: boolean) => setUIState(prev => ({ ...prev, showCartToast: value }));

  useEffect(() => {
    const payload: SessionAppRoutePersisted = {
      v: 1,
      currentPage,
      navigationHistory,
      searchQuery,
      selectedCategory,
      selectedAdId,
      selectedSellerId,
      selectedSellerName,
      selectedConversationId,
      selectedOrderId,
      selectedOfferId,
      orderDetailReturnPage,
      lastOrderNumber,
      returnToPage,
    };
    persistSessionAppRoute(payload);
  }, [
    currentPage,
    navigationHistory,
    searchQuery,
    selectedCategory,
    selectedAdId,
    selectedSellerId,
    selectedSellerName,
    selectedConversationId,
    selectedOrderId,
    selectedOfferId,
    orderDetailReturnPage,
    lastOrderNumber,
    returnToPage,
  ]);

  return {
    ads,
    setAds,
    isModalOpen: uiState.isModalOpen,
    setIsModalOpen,
    searchQuery,
    setSearchQuery,
    selectedCategory,
    setSelectedCategory,
    searchResultsAds,
    searchNextCursor,
    searchResultsLoading,
    loadSearchResults,
    loadMoreSearchResults,
    categoriesWithAds,
    categoriesWithAdsRanked,
    categoryCatalog,
    categoriesWithAdsFetchedAt,
    loadCategoriesWithAds,
    invalidateCategoriesWithAds,
    isMenuOpen: uiState.isMenuOpen,
    setIsMenuOpen,
    isChatModalOpen: uiState.isChatModalOpen,
    setIsChatModalOpen,
    favoriteItems,
    setFavoriteItems,
    favoriteAdIds,
    setFavoriteAdIds,
    favoriteCreatedAt,
    setFavoriteCreatedAt,
    cart,
    setCart,
    isLoggedIn,
    setIsLoggedIn,
    currentPage,
    setCurrentPage,
    navigationHistory,
    setNavigationHistory,
    returnToPage,
    setReturnToPage,
    selectedAdId,
    setSelectedAdId,
    selectedOrderId,
    setSelectedOrderId,
    orderDetailReturnPage,
    setOrderDetailReturnPage,
    selectedSellerName,
    setSelectedSellerName,
    selectedSellerId,
    setSelectedSellerId,
    selectedCity,
    setSelectedCity,
    isLocationSelectorOpen: uiState.isLocationSelectorOpen,
    setIsLocationSelectorOpen,
    conversations,
    setConversations,
    selectedConversationId,
    setSelectedConversationId,
    chatMessages,
    setChatMessages,
    receivedOffers,
    setReceivedOffers,
    sentOffers,
    setSentOffers,
    selectedOfferId,
    setSelectedOfferId,
    addresses,
    setAddresses,
    selectedAddressId,
    setSelectedAddressId,
    paymentMethods,
    setPaymentMethods,
    selectedPaymentId,
    setSelectedPaymentId,
    shippingOptions,
    setShippingOptions,
    shippingOptionsLoading,
    setShippingOptionsLoading,
    shippingOptionsError,
    setShippingOptionsError,
    hubDisplay,
    setHubDisplay,
    selectedShippingId,
    setSelectedShippingId,
    selectedInstallments,
    setSelectedInstallments,
    lastOrderNumber,
    setLastOrderNumber,
    orderError,
    setOrderError,
    orderConfirming,
    setOrderConfirming,
    pendingPaymentOrderId,
    setPendingPaymentOrderId,
    pendingPaymentClientSecret,
    setPendingPaymentClientSecret,
    pendingPaymentPublishableKey,
    setPendingPaymentPublishableKey,
    secondPartyPaymentOrderId,
    setSecondPartyPaymentOrderId,
    payOrderId,
    setPayOrderId,
    cartOrderId,
    setCartOrderId,
    showCartToast: uiState.showCartToast,
    setShowCartToast,
    cartToastProduct,
    setCartToastProduct,
    cartBlockMessage,
    setCartBlockMessage,
    currentSort,
    setCurrentSort,
    isSortSheetOpen: uiState.isSortSheetOpen,
    setIsSortSheetOpen,
    customFilters,
    setCustomFilters,
    isCustomFilterOpen: uiState.isCustomFilterOpen,
    setIsCustomFilterOpen,
    navigatedFromMenu,
    setNavigatedFromMenu,
    userProfile,
    setUserProfile,
    userId,
    setUserId,
    userEmail,
    setUserEmail,
    userName,
    setUserName,
    authEmailVerified,
    setAuthEmailVerified,
    userProfilePicture,
    setUserProfilePicture,
    needsOnboarding,
    setNeedsOnboarding,
    browsingAfterSignup,
    setBrowsingAfterSignup,
    authCheckPending,
    setAuthCheckPending,
    darkMode,
    notificationUnreadCount,
    setNotificationUnreadCount,
  };
}